import { Link, Navigate, useLocation } from "react-router-dom";
import { Star, Loader2, Inbox } from "lucide-react";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FORMAT_STYLES = {
  CDR: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  PSD: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  AI: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  CANVA: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  PDF: "bg-red-500/15 text-red-400 border-red-500/30",
};

export const FormatBadge = ({ f, className = "" }) => (
  <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono-ui text-[10px] font-medium tracking-wider ${FORMAT_STYLES[f] || "bg-secondary text-secondary-foreground border-border"} ${className}`}>
    {f === "CANVA" ? "Canva" : f}
  </span>
);

export const Stars = ({ value = 0, count, size = 14 }) => (
  <span className="inline-flex items-center gap-1 text-sm">
    <Star size={size} className={value > 0 ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />
    <span className="font-semibold">{value ? Number(value).toFixed(1) : "New"}</span>
    {count != null && <span className="text-muted-foreground">({count})</span>}
  </span>
);

const STATUS = {
  approved: "bg-emerald-500/15 text-emerald-500", completed: "bg-emerald-500/15 text-emerald-500", paid: "bg-emerald-500/15 text-emerald-500",
  success: "bg-emerald-500/15 text-emerald-500", resolved: "bg-emerald-500/15 text-emerald-500", active: "bg-emerald-500/15 text-emerald-500",
  pending: "bg-amber-500/15 text-amber-500", open: "bg-amber-500/15 text-amber-500", initiated: "bg-amber-500/15 text-amber-500",
  processing: "bg-sky-500/15 text-sky-400",
  rejected: "bg-rose-500/15 text-rose-500", failed: "bg-rose-500/15 text-rose-500", removed: "bg-rose-500/15 text-rose-500",
  suspended: "bg-rose-500/15 text-rose-500", reversed: "bg-slate-500/15 text-slate-400", dismissed: "bg-slate-500/15 text-slate-400",
};
export const StatusBadge = ({ s, testid }) => (
  <span data-testid={testid} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS[s] || "bg-secondary"}`}>
    {s === "approved" && "Approved"}{s !== "approved" && (s || "").replace(/_/g, " ")}
  </span>
);

export const CategoryIcon = ({ name, size = 18, className = "" }) => {
  const I = Icons[name] || Icons.Shapes;
  return <I size={size} className={className} />;
};

export const Spinner = ({ className = "" }) => (
  <div className={`flex items-center justify-center py-20 ${className}`}><Loader2 className="animate-spin text-primary" /></div>
);

export const EmptyState = ({ title, text, action, testid = "empty-state" }) => (
  <div data-testid={testid} className="card-surface flex flex-col items-center gap-3 px-6 py-16 text-center">
    <div className="rounded-2xl bg-secondary p-4"><Inbox className="text-muted-foreground" /></div>
    <h3 className="text-lg font-semibold">{title}</h3>
    {text && <p className="max-w-sm text-sm text-muted-foreground">{text}</p>}
    {action}
  </div>
);

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 className="text-3xl font-bold sm:text-4xl">{title}</h1>
    </div>
    {children}
  </div>
);

export const StatCard = ({ label, value, icon: I, accent, testid }) => (
  <div data-testid={testid} className="card-surface p-5">
    <div className="flex items-center justify-between">
      <p className="eyebrow">{label}</p>
      {I && <I size={18} className={accent || "text-muted-foreground"} />}
    </div>
    <p className="mt-3 font-display text-2xl font-bold sm:text-3xl">{value}</p>
  </div>
);

export function Protected({ children, role }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

export const Logo = ({ testid = "logo-link" }) => (
  <Link to="/" data-testid={testid} className="flex items-center gap-2">
    <span className="relative grid h-8 w-8 place-items-center rounded-lg bg-primary font-display text-lg font-extrabold text-white">
      G<span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-accent" />
    </span>
    <span className="font-display text-base font-bold tracking-tight sm:text-lg">Grafix<span className="text-primary">Market</span></span>
  </Link>
);
